import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Send, Clock, CheckCircle, XCircle, Calendar, Trash2, MessageSquare, Plus, UserPlus, UserMinus, Upload, ContactRound, Edit2, X, Search, Eye, EyeOff, Filter, RotateCcw, CheckSquare, Square } from 'lucide-react';
import { API } from '../App';
import SiteSettingsContext from '../context/SiteSettingsContext';

// Helper to format time to 12-hour AM/PM format
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
    return timeStr;
  }
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeStr;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function SMSDashboard() {
  const { settings } = useContext(SiteSettingsContext);
  const timezone = settings?.timezone || 'America/Los_Angeles';
  
  const [activeTab, setActiveTab] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const fileInputRef = useRef(null);
  const bulkUploadRef = useRef(null);
  const csvMappingRef = useRef(null);

  // Filter and search states
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);

  // CSV Mapping states
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvMapping, setCsvMapping] = useState({ 
    first_name_column: '', 
    last_name_column: '', 
    phone1_column: '', 
    phone2_column: '',
    email_column: '', 
    notes_column: '' 
  });
  const [mappingGroupId, setMappingGroupId] = useState(null);
  const [csvFileData, setCsvFileData] = useState(null);  // Store parsed CSV data

  // Multi-select states for bulk delete
  const [selectedMembers, setSelectedMembers] = useState({});
  const [recentImports, setRecentImports] = useState({});

  // Contact multi-select and import states
  const [selectedContacts, setSelectedContacts] = useState({});
  const [contactImports, setContactImports] = useState([]);
  const [contactCsvPreview, setContactCsvPreview] = useState(null);
  const [contactCsvMapping, setContactCsvMapping] = useState({ 
    first_name_column: '', 
    last_name_column: '', 
    phone1_column: '', 
    phone2_column: '',
    email_column: '', 
    notes_column: '' 
  });
  const contactCsvRef = useRef(null);

  // SMS Opt-out states
  const [optouts, setOptouts] = useState([]);
  const [newOptoutPhone, setNewOptoutPhone] = useState('');

  // Vonage SMS account status (balance/configuration warnings)
  const [smsAccountStatus, setSmsAccountStatus] = useState(null);

  // Form states
  const [messageForm, setMessageForm] = useState({
    group_ids: [],
    message: '',
    scheduled_date: '',
    scheduled_time: '',
    send_to_all: false
  });

  const [groupForm, setGroupForm] = useState({
    name: '',
    description: ''
  });

  const [memberForm, setMemberForm] = useState({
    name: '',
    phone: ''
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    fetchData();
    fetchAvailableMonths();
    fetchSmsAccountStatus();
  }, []);

  const fetchSmsAccountStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/sms/account-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmsAccountStatus(res.data);
    } catch (e) {
      // Non-fatal: just don't show the banner
      setSmsAccountStatus(null);
    }
  };

  useEffect(() => {
    fetchMessageHistory();
  }, [selectedMonth, showHidden]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [groupsRes, scheduledRes, historyRes, contactsRes] = await Promise.all([
        axios.get(`${API}/sms/groups`, config),
        axios.get(`${API}/sms/messages/scheduled`, config),
        axios.get(`${API}/sms/messages/history`, config),
        axios.get(`${API}/contacts`, config)
      ]);

      setGroups(groupsRes.data);
      setScheduledMessages(scheduledRes.data);
      setMessageHistory(historyRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Failed to load SMS data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMonths = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/sms/messages/months`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableMonths(res.data);
    } catch (error) {
      console.error('Failed to fetch months');
    }
  };

  const fetchMessageHistory = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API}/sms/messages/history?include_hidden=${showHidden}`;
      if (selectedMonth) {
        url += `&month=${selectedMonth}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch message history');
    }
  };

  const handleSearchMessages = async () => {
    if (!searchQuery.trim()) {
      fetchMessageHistory();
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/sms/messages/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageHistory(res.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleHideMessage = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/sms/messages/${messageId}/hide`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message hidden');
      fetchMessageHistory();
    } catch (error) {
      toast.error('Failed to hide message');
    }
  };

  const handleUnhideMessage = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/sms/messages/${messageId}/unhide`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message restored');
      fetchMessageHistory();
    } catch (error) {
      toast.error('Failed to unhide message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Permanently delete this message? This cannot be undone.')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted permanently');
      fetchMessageHistory();
      fetchAvailableMonths();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleBulkUpload = async (e, groupId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setBulkUploading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/sms/groups/${groupId}/bulk-upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Bulk upload complete: ${res.data.added} added, ${res.data.skipped} skipped`);
      
      if (res.data.errors?.length > 0) {
        console.warn('Bulk upload errors:', res.data.errors);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
      if (bulkUploadRef.current) {
        bulkUploadRef.current.value = '';
      }
    }
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return 'All Time';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // CSV Preview and Mapping
  const handleCsvPreview = async (e, groupId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      e.target.value = '';  // Reset input
      return;
    }

    try {
      // Read and parse CSV locally first
      const text = await file.text();
      const parsedData = parseCSV(text);
      
      if (parsedData.rows.length === 0) {
        toast.error('CSV file is empty or has no data rows');
        e.target.value = '';  // Reset input
        return;
      }
      
      setCsvPreview({
        columns: parsedData.headers,
        preview_rows: parsedData.rows.slice(0, 5),
        total_rows: parsedData.rows.length
      });
      setCsvFileData(parsedData.rows);  // Store all parsed rows
      setMappingGroupId(groupId);
      setCsvMapping({ 
        first_name_column: '', 
        last_name_column: '', 
        phone1_column: '', 
        phone2_column: '',
        email_column: '', 
        notes_column: '' 
      });
      
      toast.success(`CSV loaded: ${parsedData.rows.length} rows, ${parsedData.headers.length} columns`);
      
    } catch (error) {
      toast.error('Failed to parse CSV file: ' + error.message);
      console.error('CSV parse error:', error);
    }
    
    e.target.value = '';  // Reset input so same file can be selected again
  };

  // Parse CSV properly handling quotes and commas
  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return { headers: [], rows: [] };
    
    const parseRow = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim().replace(/^"|"$/g, ''));
      return values;
    };
    
    const headers = parseRow(lines[0]);
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;  // Skip empty lines
      const values = parseRow(lines[i]);
      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });
      rows.push(row);
    }
    
    return { headers, rows };
  };

  const handleMappedUpload = async () => {
    if (!csvMapping.first_name_column || !csvMapping.phone1_column) {
      toast.error('Please select at least First Name and Phone 1 columns');
      return;
    }

    if (!csvPreview || !mappingGroupId || !csvFileData) return;

    setBulkUploading(true);
    const token = localStorage.getItem('token');

    try {
      // Transform rows to combine first/last name and handle multiple phones
      const transformedRows = [];
      
      csvFileData.forEach(row => {
        const firstName = (row[csvMapping.first_name_column] || '').trim();
        const lastName = csvMapping.last_name_column ? (row[csvMapping.last_name_column] || '').trim() : '';
        const fullName = `${firstName} ${lastName}`.trim();
        const phone1 = (row[csvMapping.phone1_column] || '').trim();
        const phone2 = csvMapping.phone2_column ? (row[csvMapping.phone2_column] || '').trim() : '';
        
        // Skip rows without a name or phone
        if (!fullName && !phone1) return;
        
        // Add entry for phone 1 only if it has a value
        if (phone1) {
          transformedRows.push({
            name: fullName || 'Unknown',
            phone: phone1
          });
        }
        
        // Add separate entry for phone 2 ONLY if it exists, has a value, and is different from phone 1
        if (phone2 && phone2.length > 0 && phone2 !== phone1) {
          transformedRows.push({
            name: fullName || 'Unknown',
            phone: phone2
          });
        }
      });

      const res = await axios.post(`${API}/sms/groups/${mappingGroupId}/bulk-upload-mapped`, {
        rows: transformedRows,
        name_column: 'name',
        phone_column: 'phone'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Upload complete: ${res.data.added} added, ${res.data.skipped} skipped`);
      
      if (res.data.errors && res.data.errors.length > 0) {
        console.warn('Upload errors:', res.data.errors);
      }
      
      // Clear mapping state
      setCsvPreview(null);
      setMappingGroupId(null);
      setCsvMapping({ 
        first_name_column: '', 
        last_name_column: '', 
        phone1_column: '', 
        phone2_column: '',
        email_column: '', 
        notes_column: '' 
      });
      setCsvFileData(null);
      
      // Fetch recent imports for undo
      fetchRecentImports(mappingGroupId);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const cancelMapping = () => {
    setCsvPreview(null);
    setMappingGroupId(null);
    setCsvMapping({ 
      first_name_column: '', 
      last_name_column: '', 
      phone1_column: '', 
      phone2_column: '',
      email_column: '', 
      notes_column: '' 
    });
    setCsvFileData(null);
  };

  // Fetch recent imports for a group
  const fetchRecentImports = async (groupId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/sms/groups/${groupId}/imports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentImports(prev => ({ ...prev, [groupId]: res.data }));
    } catch (error) {
      console.error('Failed to fetch imports');
    }
  };

  // Undo an import
  const handleUndoImport = async (groupId, importId) => {
    if (!window.confirm('Undo this import? All members from this import will be removed.')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await axios.delete(`${API}/sms/groups/${groupId}/imports/${importId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchRecentImports(groupId);
      fetchData();
    } catch (error) {
      toast.error('Failed to undo import');
    }
  };

  // Multi-select handlers
  const toggleMemberSelection = (groupId, phone) => {
    setSelectedMembers(prev => {
      const groupSelections = prev[groupId] || {};
      return {
        ...prev,
        [groupId]: {
          ...groupSelections,
          [phone]: !groupSelections[phone]
        }
      };
    });
  };

  const selectAllMembers = (groupId, members) => {
    const allSelected = members.every(m => selectedMembers[groupId]?.[m.phone]);
    if (allSelected) {
      // Deselect all
      setSelectedMembers(prev => ({ ...prev, [groupId]: {} }));
    } else {
      // Select all
      const selections = {};
      members.forEach(m => { selections[m.phone] = true; });
      setSelectedMembers(prev => ({ ...prev, [groupId]: selections }));
    }
  };

  const getSelectedCount = (groupId) => {
    const groupSelections = selectedMembers[groupId] || {};
    return Object.values(groupSelections).filter(Boolean).length;
  };

  const handleBulkDeleteMembers = async (groupId) => {
    const groupSelections = selectedMembers[groupId] || {};
    const phonesToDelete = Object.entries(groupSelections)
      .filter(([_, selected]) => selected)
      .map(([phone]) => phone);
    
    if (phonesToDelete.length === 0) {
      toast.error('No members selected');
      return;
    }

    if (!window.confirm(`Delete ${phonesToDelete.length} selected members?`)) return;

    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/sms/groups/${groupId}/bulk-delete`, {
        phones: phonesToDelete
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setSelectedMembers(prev => ({ ...prev, [groupId]: {} }));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete members');
    }
  };

  const handleDeleteAllMembers = async (groupId) => {
    if (!window.confirm('DELETE ALL MEMBERS from this group? This cannot be undone!')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await axios.delete(`${API}/sms/groups/${groupId}/members/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setSelectedMembers(prev => ({ ...prev, [groupId]: {} }));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete all members');
    }
  };

  // Contact handlers
  const handleContactCsvPreview = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/contacts/preview-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setContactCsvPreview(res.data);
      setContactCsvMapping({ 
        first_name_column: '', 
        last_name_column: '', 
        phone1_column: '', 
        phone2_column: '',
        email_column: '', 
        notes_column: '' 
      });
      contactCsvRef.current = file;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview CSV');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContactMappedUpload = async () => {
    if (!contactCsvMapping.first_name_column || !contactCsvMapping.phone1_column) {
      toast.error('Please select at least First Name and Phone 1 columns');
      return;
    }

    if (!contactCsvPreview) return;

    setBulkUploading(true);
    const token = localStorage.getItem('token');

    try {
      const file = contactCsvRef.current;
      const text = await file.text();
      
      // Use the same robust parsing as SMS groups
      const parsedData = parseCSV(text);
      
      // Transform rows to combine first/last name and handle multiple phones
      const transformedRows = [];
      
      parsedData.rows.forEach(row => {
        const firstName = (row[contactCsvMapping.first_name_column] || '').trim();
        const lastName = contactCsvMapping.last_name_column ? (row[contactCsvMapping.last_name_column] || '').trim() : '';
        const fullName = `${firstName} ${lastName}`.trim();
        const phone1 = (row[contactCsvMapping.phone1_column] || '').trim();
        const phone2 = contactCsvMapping.phone2_column ? (row[contactCsvMapping.phone2_column] || '').trim() : '';
        const email = contactCsvMapping.email_column ? (row[contactCsvMapping.email_column] || '').trim() : '';
        const notes = contactCsvMapping.notes_column ? (row[contactCsvMapping.notes_column] || '').trim() : '';
        
        // Skip rows without a name or phone
        if (!fullName && !phone1) return;
        
        // Add entry for phone 1 only if it has a value
        if (phone1) {
          transformedRows.push({
            name: fullName || 'Unknown',
            phone: phone1,
            email: email,
            notes: notes
          });
        }
        
        // Add separate entry for phone 2 ONLY if it exists, has a value, and is different from phone 1
        if (phone2 && phone2.length > 0 && phone2 !== phone1) {
          transformedRows.push({
            name: fullName || 'Unknown',
            phone: phone2,
            email: email,
            notes: notes
          });
        }
      });

      const res = await axios.post(`${API}/contacts/bulk-upload-mapped`, {
        rows: transformedRows,
        name_column: 'name',
        phone_column: 'phone',
        email_column: 'email',
        notes_column: 'notes'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Upload complete: ${res.data.added} added, ${res.data.skipped} skipped`);
      
      setContactCsvPreview(null);
      setContactCsvMapping({ 
        first_name_column: '', 
        last_name_column: '', 
        phone1_column: '', 
        phone2_column: '',
        email_column: '', 
        notes_column: '' 
      });
      fetchContactImports();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const cancelContactMapping = () => {
    setContactCsvPreview(null);
    setContactCsvMapping({ 
      first_name_column: '', 
      last_name_column: '', 
      phone1_column: '', 
      phone2_column: '',
      email_column: '', 
      notes_column: '' 
    });
  };

  const fetchContactImports = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/contacts/imports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactImports(res.data);
    } catch (error) {
      console.error('Failed to fetch contact imports');
    }
  };

  const handleUndoContactImport = async (importId) => {
    if (!window.confirm('Undo this import? All contacts from this import will be removed.')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await axios.delete(`${API}/contacts/imports/${importId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchContactImports();
      fetchData();
    } catch (error) {
      toast.error('Failed to undo import');
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const selectAllContacts = () => {
    const allSelected = contacts.every(c => selectedContacts[c.id]);
    if (allSelected) {
      setSelectedContacts({});
    } else {
      const selections = {};
      contacts.forEach(c => { selections[c.id] = true; });
      setSelectedContacts(selections);
    }
  };

  const getSelectedContactCount = () => {
    return Object.values(selectedContacts).filter(Boolean).length;
  };

  const handleBulkDeleteContacts = async () => {
    const idsToDelete = Object.entries(selectedContacts)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    if (idsToDelete.length === 0) {
      toast.error('No contacts selected');
      return;
    }

    if (!window.confirm(`Delete ${idsToDelete.length} selected contacts?`)) return;

    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/contacts/bulk-delete`, {
        contact_ids: idsToDelete
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setSelectedContacts({});
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contacts');
    }
  };

  const handleDeleteAllContacts = async () => {
    if (!window.confirm('DELETE ALL CONTACTS? This cannot be undone!')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await axios.delete(`${API}/contacts/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setSelectedContacts({});
      fetchData();
    } catch (error) {
      toast.error('Failed to delete all contacts');
    }
  };

  // SMS Opt-out handlers
  const fetchOptouts = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/sms/optouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOptouts(res.data);
    } catch (error) {
      console.error('Failed to fetch opt-outs');
    }
  };

  const handleAddOptout = async (e) => {
    e.preventDefault();
    if (!newOptoutPhone.trim()) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/sms/optouts?phone=${encodeURIComponent(newOptoutPhone)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number added to opt-out list');
      setNewOptoutPhone('');
      fetchOptouts();
    } catch (error) {
      toast.error('Failed to add opt-out');
    }
  };

  const handleRemoveOptout = async (phone) => {
    if (!window.confirm('Restore this phone number? They will be able to receive SMS again.')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/optouts/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number restored - can receive SMS again');
      fetchOptouts();
    } catch (error) {
      toast.error('Failed to restore phone number');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${API}/sms/groups`, groupForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group created successfully!');
      setGroupForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? All members will be removed.')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group deleted');
      setSelectedGroup(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleAddMember = async (e, groupId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${API}/sms/groups/${groupId}/members`, memberForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member added!');
      setMemberForm({ name: '', phone: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add member');
    }
  };

  const handleRemoveMember = async (groupId, phone) => {
    if (!window.confirm('Remove this member from the group?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/groups/${groupId}/members/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if ((!messageForm.group_ids.length && !messageForm.send_to_all) || !messageForm.message) {
      toast.error('Please select at least one group and enter a message');
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      // Build the scheduled_for datetime if both date and time are provided
      let scheduled_for = '';
      if (messageForm.scheduled_date && messageForm.scheduled_time) {
        // Combine date and time into a datetime string
        // The backend will interpret this in the site's timezone
        scheduled_for = `${messageForm.scheduled_date}T${messageForm.scheduled_time}`;
      }
      
      const payload = {
        group_ids: messageForm.group_ids,
        message: messageForm.message,
        send_to_all: messageForm.send_to_all,
        scheduled_for: scheduled_for || null,
        timezone: timezone
      };
      
      const response = await axios.post(`${API}/sms/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (scheduled_for) {
        // Format the scheduled time for display
        const scheduledDate = new Date(`${messageForm.scheduled_date}T${messageForm.scheduled_time}`);
        const displayTime = scheduledDate.toLocaleString('en-US', { 
          dateStyle: 'medium', 
          timeStyle: 'short',
          hour12: true 
        });
        toast.success(`Message scheduled for ${displayTime} (${timezone.replace('_', ' ')})`);
      } else {
        const sent = response.data.sent_count ?? 0;
        const failed = response.data.failed_count ?? 0;
        if (response.data.status === 'partial') {
          toast.warning(`Partially delivered: ${sent} sent, ${failed} failed`, {
            description: (response.data.delivery_errors || []).join('; ') || undefined,
            duration: 8000
          });
        } else {
          toast.success(`Message sent to ${sent} recipients!`);
        }
      }
      
      setMessageForm({ group_ids: [], message: '', scheduled_date: '', scheduled_time: '', send_to_all: false });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Failed to send message';
      toast.error('SMS delivery failed', {
        description: detail,
        duration: 10000
      });
    }
  };

  // Contact Management Handlers
  const handleCreateContact = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact}`, contactForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact updated!');
        setEditingContact(null);
      } else {
        await axios.post(`${API}/contacts`, contactForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact added!');
      }
      setContactForm({ name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact.id);
    setContactForm({
      name: contact.name,
      phone: contact.phone || '',
      email: contact.email || ''
    });
  };

  const handleCancelEditContact = () => {
    setEditingContact(null);
    setContactForm({ name: '', phone: '', email: '' });
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or has no data rows');
        return;
      }

      // Parse header to find column indices
      const header = lines[0].toLowerCase().split(',').map(h => h.trim());
      const nameIdx = header.findIndex(h => h.includes('name'));
      const phoneIdx = header.findIndex(h => h.includes('phone'));
      const emailIdx = header.findIndex(h => h.includes('email'));

      if (nameIdx === -1) {
        toast.error('CSV must have a "name" column');
        return;
      }

      const contactsToImport = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
        const contact = {
          name: values[nameIdx] || '',
          phone: phoneIdx !== -1 ? values[phoneIdx] : null,
          email: emailIdx !== -1 ? values[emailIdx] : null
        };
        if (contact.name) {
          contactsToImport.push(contact);
        }
      }

      if (contactsToImport.length === 0) {
        toast.error('No valid contacts found in CSV');
        return;
      }

      const token = localStorage.getItem('token');
      try {
        const response = await axios.post(`${API}/contacts/bulk`, { contacts: contactsToImport }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(response.data.message);
        fetchData();
      } catch (error) {
        toast.error('Failed to import contacts');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset file input
  };

  const handleGroupSelect = (groupId) => {
    setMessageForm(prev => {
      const newIds = prev.group_ids.includes(groupId)
        ? prev.group_ids.filter(id => id !== groupId)
        : [...prev.group_ids, groupId];
      return { ...prev, group_ids: newIds, send_to_all: false };
    });
  };

  const handleSelectAllGroups = () => {
    setMessageForm(prev => ({
      ...prev,
      send_to_all: !prev.send_to_all,
      group_ids: []
    }));
  };

  const getTotalRecipients = () => {
    if (messageForm.send_to_all) {
      const allPhones = new Set();
      groups.forEach(g => g.members?.forEach(m => allPhones.add(m.phone)));
      return allPhones.size;
    }
    const selectedPhones = new Set();
    messageForm.group_ids.forEach(gid => {
      const group = groups.find(g => g.id === gid);
      group?.members?.forEach(m => selectedPhones.add(m.phone));
    });
    return selectedPhones.size;
  };

  const handleCancelScheduled = async (messageId) => {
    if (!window.confirm('Cancel this scheduled message?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduled message cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel message');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getGroupById = (groupId) => {
    return groups.find(g => g.id === groupId);
  };

  if (loading) {
    return <div className="text-center py-12"><p className="text-2xl" style={{ color: '#1e3a8a' }}>Loading SMS Dashboard...</p></div>;
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-3 bg-white rounded-xl p-4 shadow-md">
        <button
          onClick={() => setActiveTab('contacts')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
            activeTab === 'contacts' 
              ? 'bg-blue-900 text-white shadow-lg' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="sms-tab-contacts"
        >
          <ContactRound size={20} />
          Contacts ({contacts.length})
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
            activeTab === 'groups' 
              ? 'bg-blue-900 text-white shadow-lg' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="sms-tab-groups"
        >
          <Users size={20} />
          SMS Groups ({groups.length})
        </button>
        <button
          onClick={() => setActiveTab('send')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
            activeTab === 'send' 
              ? 'bg-blue-900 text-white shadow-lg' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="sms-tab-send"
        >
          <Send size={20} />
          Send Message
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
            activeTab === 'scheduled' 
              ? 'bg-blue-900 text-white shadow-lg' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="sms-tab-scheduled"
        >
          <Clock size={20} />
          Scheduled ({scheduledMessages.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
            activeTab === 'history' 
              ? 'bg-blue-900 text-white shadow-lg' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="sms-tab-history"
        >
          <MessageSquare size={20} />
          History ({messageHistory.length})
        </button>
        <button
          onClick={() => { setActiveTab('optouts'); fetchOptouts(); }}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
            activeTab === 'optouts' 
              ? 'bg-red-600 text-white shadow-lg' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="sms-tab-optouts"
        >
          <UserMinus size={20} />
          Opt-Outs ({optouts.length})
        </button>
      </div>

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Add/Edit Contact */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-md sticky top-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <ContactRound size={28} color="#1e3a8a" />
                  <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                    {editingContact ? 'Edit Contact' : 'Add Contact'}
                  </h3>
                </div>
                {editingContact && (
                  <button onClick={handleCancelEditContact} className="text-gray-500 hover:text-gray-700">
                    <X size={24} />
                  </button>
                )}
              </div>

              <form onSubmit={handleCreateContact} className="space-y-4">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Name *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg"
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Phone</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg"
                    placeholder="+15419990000"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Email</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg"
                    placeholder="email@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <button type="submit" className="btn-primary w-full" data-testid="save-contact-btn">
                  {editingContact ? (
                    <><Edit2 size={20} className="inline mr-2" />Update Contact</>
                  ) : (
                    <><Plus size={20} className="inline mr-2" />Add Contact</>
                  )}
                </button>
              </form>

              {/* CSV Upload with Mapping */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-lg font-bold mb-3" style={{ color: '#1e3a8a' }}>
                  <Upload size={18} className="inline mr-2" />
                  Import from CSV
                </h4>
                <p className="text-sm text-slate-600 mb-3">Upload a CSV - you'll map columns before importing</p>
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleContactCsvPreview}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary w-full"
                  data-testid="csv-upload-btn"
                >
                  <Upload size={20} className="inline mr-2" />
                  Upload CSV File
                </button>

                {/* Recent Imports - Undo */}
                {contactImports.length > 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h6 className="text-sm font-semibold text-yellow-800 mb-2">
                      <RotateCcw size={14} className="inline mr-1" />
                      Recent Imports (Undo)
                    </h6>
                    <div className="space-y-2">
                      {contactImports.slice(0, 3).map((imp) => (
                        <div key={imp.id} className="flex justify-between items-center text-sm">
                          <span className="text-yellow-700">
                            {imp.count} contacts - {new Date(imp.imported_at).toLocaleDateString()}
                          </span>
                          <button
                            onClick={() => handleUndoContactImport(imp.id)}
                            className="text-red-600 hover:text-red-800 text-xs font-semibold"
                          >
                            Undo
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={fetchContactImports}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  <RotateCcw size={14} className="inline mr-1" />
                  Check for Undo Options
                </button>
              </div>
            </div>
          </div>

          {/* CSV Mapping Modal for Contacts */}
          {contactCsvPreview && (
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-blue-400">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>Map CSV Columns</h4>
                  <button onClick={cancelContactMapping} className="text-gray-500 hover:text-gray-700">
                    <X size={24} />
                  </button>
                </div>
                
                <p className="text-sm text-slate-600 mb-4">
                  Found {contactCsvPreview.total_rows} rows. Map the columns below (First Name and Phone 1 are required):
                </p>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Name Fields Row */}
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-green-700">First Name *</label>
                    <select
                      value={contactCsvMapping.first_name_column}
                      onChange={(e) => setContactCsvMapping({ ...contactCsvMapping, first_name_column: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-green-300 rounded-lg bg-green-50"
                    >
                      <option value="">-- Select --</option>
                      {contactCsvPreview.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-green-600">Last Name (optional)</label>
                    <select
                      value={contactCsvMapping.last_name_column}
                      onChange={(e) => setContactCsvMapping({ ...contactCsvMapping, last_name_column: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-green-200 rounded-lg bg-green-50"
                    >
                      <option value="">-- None --</option>
                      {contactCsvPreview.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-purple-700">Email (optional)</label>
                    <select
                      value={contactCsvMapping.email_column}
                      onChange={(e) => setContactCsvMapping({ ...contactCsvMapping, email_column: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg bg-purple-50"
                    >
                      <option value="">-- None --</option>
                      {contactCsvPreview.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Phone Fields Row */}
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-blue-700">Phone 1 *</label>
                    <select
                      value={contactCsvMapping.phone1_column}
                      onChange={(e) => setContactCsvMapping({ ...contactCsvMapping, phone1_column: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg bg-blue-50"
                    >
                      <option value="">-- Select --</option>
                      {contactCsvPreview.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-blue-600">Phone 2 (optional)</label>
                    <select
                      value={contactCsvMapping.phone2_column}
                      onChange={(e) => setContactCsvMapping({ ...contactCsvMapping, phone2_column: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg bg-blue-50"
                    >
                      <option value="">-- None --</option>
                      {contactCsvPreview.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-orange-700">Notes (optional)</label>
                    <select
                      value={contactCsvMapping.notes_column}
                      onChange={(e) => setContactCsvMapping({ ...contactCsvMapping, notes_column: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-orange-300 rounded-lg bg-orange-50"
                    >
                      <option value="">-- None --</option>
                      {contactCsvPreview.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="overflow-x-auto mb-4 max-h-64 overflow-y-auto">
                  <p className="text-sm font-semibold mb-2">Preview (first 5 rows):</p>
                  <table className="min-w-full text-sm border">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        {contactCsvPreview.columns.map((col) => (
                          <th key={col} className={`px-3 py-2 border text-left font-semibold ${
                            col === contactCsvMapping.first_name_column ? 'bg-green-200 text-green-800' :
                            col === contactCsvMapping.last_name_column ? 'bg-green-100 text-green-700' :
                            col === contactCsvMapping.phone1_column ? 'bg-blue-200 text-blue-800' :
                            col === contactCsvMapping.phone2_column ? 'bg-blue-100 text-blue-700' :
                            col === contactCsvMapping.email_column ? 'bg-purple-200 text-purple-800' :
                            col === contactCsvMapping.notes_column ? 'bg-orange-200 text-orange-800' : ''
                          }`}>
                            {col}
                            {col === contactCsvMapping.first_name_column && <span className="ml-1">(First)</span>}
                            {col === contactCsvMapping.last_name_column && <span className="ml-1">(Last)</span>}
                            {col === contactCsvMapping.phone1_column && <span className="ml-1">(Phone 1)</span>}
                            {col === contactCsvMapping.phone2_column && <span className="ml-1">(Phone 2)</span>}
                            {col === contactCsvMapping.email_column && <span className="ml-1">(Email)</span>}
                            {col === contactCsvMapping.notes_column && <span className="ml-1">(Notes)</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {contactCsvPreview.preview_rows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {contactCsvPreview.columns.map((col) => (
                            <td key={col} className={`px-3 py-2 border ${
                              col === contactCsvMapping.first_name_column ? 'bg-green-50' :
                              col === contactCsvMapping.last_name_column ? 'bg-green-50' :
                              col === contactCsvMapping.phone1_column ? 'bg-blue-50' :
                              col === contactCsvMapping.phone2_column ? 'bg-blue-50' :
                              col === contactCsvMapping.email_column ? 'bg-purple-50' :
                              col === contactCsvMapping.notes_column ? 'bg-orange-50' : ''
                            }`}>
                              {row[col] || <span className="text-gray-400 italic">empty</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleContactMappedUpload}
                    disabled={!contactCsvMapping.first_name_column || !contactCsvMapping.phone1_column || bulkUploading}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    {bulkUploading ? 'Importing...' : `Import ${contactCsvPreview.total_rows} Contacts`}
                  </button>
                  <button
                    onClick={cancelContactMapping}
                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Right: Contacts List */}
          {!contactCsvPreview && (
            <div className="lg:col-span-2 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>All Contacts ({contacts.length})</h3>
                {contacts.length > 0 && (
                  <div className="flex gap-2">
                    {getSelectedContactCount() > 0 && (
                      <button
                        onClick={handleBulkDeleteContacts}
                        className="bg-red-100 text-red-600 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-200"
                      >
                        <Trash2 size={16} className="inline mr-1" />
                        Delete Selected ({getSelectedContactCount()})
                      </button>
                    )}
                    <button
                      onClick={handleDeleteAllContacts}
                      className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-700"
                    >
                      Delete All
                    </button>
                  </div>
                )}
              </div>
              
              {contacts.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-md">
                  <ContactRound size={64} color="#cbd5e1" className="mx-auto mb-4" />
                  <p className="text-xl text-slate-600">No contacts yet</p>
                  <p className="text-slate-500">Add contacts manually or import from CSV</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-blue-900 text-white">
                        <tr>
                          <th className="px-4 py-4 text-left">
                            <button
                              onClick={selectAllContacts}
                              className="flex items-center gap-2 text-white hover:text-amber-200"
                            >
                              {contacts.every(c => selectedContacts[c.id]) ? (
                                <CheckSquare size={20} />
                              ) : (
                                <Square size={20} />
                              )}
                              <span className="text-sm">({getSelectedContactCount()})</span>
                            </button>
                          </th>
                          <th className="px-4 py-4 text-left text-lg font-semibold">Name</th>
                          <th className="px-4 py-4 text-left text-lg font-semibold">Phone</th>
                          <th className="px-4 py-4 text-left text-lg font-semibold">Email</th>
                          <th className="px-4 py-4 text-right text-lg font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {contacts.map((contact) => (
                          <tr 
                            key={contact.id} 
                            className={`hover:bg-amber-50 ${selectedContacts[contact.id] ? 'bg-blue-50' : ''}`}
                            data-testid={`contact-${contact.id}`}
                          >
                            <td className="px-4 py-4">
                              <button
                                onClick={() => toggleContactSelection(contact.id)}
                                className="text-blue-600"
                              >
                                {selectedContacts[contact.id] ? (
                                  <CheckSquare size={22} />
                                ) : (
                                  <Square size={22} />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-4 text-lg font-medium">{contact.name}</td>
                            <td className="px-4 py-4 text-slate-600">{contact.phone || '-'}</td>
                            <td className="px-4 py-4 text-slate-600">{contact.email || '-'}</td>
                            <td className="px-4 py-4 text-right">
                              <button
                                onClick={() => handleEditContact(contact)}
                                className="text-blue-600 hover:text-blue-800 p-2"
                                data-testid={`edit-contact-${contact.id}`}
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact.id)}
                                className="text-red-600 hover:text-red-800 p-2 ml-2"
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Manage Groups Tab */}
      {activeTab === 'groups' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Create Group */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-md sticky top-4">
              <div className="flex items-center gap-3 mb-6">
                <Plus size={28} color="#1e3a8a" />
                <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>Create New Group</h3>
              </div>

              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Group Name *</label>
                  <input
                    type="text"
                    required
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg"
                    placeholder="e.g., Youth Group"
                    data-testid="group-name-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Description</label>
                  <textarea
                    rows={3}
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg"
                    placeholder="Optional description"
                    data-testid="group-desc-input"
                  />
                </div>
                <button type="submit" className="btn-primary w-full" data-testid="create-group-btn">
                  <Plus size={20} className="inline mr-2" />
                  Create Group
                </button>
              </form>
            </div>
          </div>

          {/* Right: Groups List */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>All Groups ({groups.length})</h3>
            
            {groups.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-md">
                <Users size={64} color="#cbd5e1" className="mx-auto mb-4" />
                <p className="text-xl text-slate-600">No groups created yet</p>
                <p className="text-slate-500">Create your first group to get started</p>
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.id} className="bg-white rounded-xl shadow-md overflow-hidden" data-testid={`group-${group.id}`}>
                  {/* Group Header */}
                  <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-6 text-white">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-2xl font-bold mb-2">{group.name}</h4>
                        {group.description && <p className="text-blue-100 text-lg">{group.description}</p>}
                        <div className="flex items-center gap-2 mt-3">
                          <Users size={18} />
                          <span className="text-lg font-semibold">{group.members?.length || 0} Members</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedGroup(selectedGroup === group.id ? null : group.id)}
                          className="bg-white text-blue-900 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition"
                          data-testid={`toggle-group-${group.id}`}
                        >
                          {selectedGroup === group.id ? 'Hide' : 'View'} Members
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group.id)}
                          className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition"
                          data-testid={`delete-group-${group.id}`}
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Members Section (Expandable) */}
                  {selectedGroup === group.id && (
                    <div className="p-6 bg-amber-50 border-t-4 border-amber-400">
                      {/* Add Member Form */}
                      <div className="bg-white rounded-lg p-4 mb-4 border-2 border-amber-300">
                        <h5 className="text-xl font-bold mb-3" style={{ color: '#1e3a8a' }}>
                          <UserPlus size={20} className="inline mr-2" />
                          Add New Member
                        </h5>
                        <form onSubmit={(e) => handleAddMember(e, group.id)} className="grid md:grid-cols-3 gap-3">
                          <input
                            type="text"
                            required
                            placeholder="Member Name"
                            value={memberForm.name}
                            onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                            className="px-4 py-2 text-lg border-2 border-amber-200 rounded-lg"
                            data-testid={`member-name-${group.id}`}
                          />
                          <input
                            type="tel"
                            required
                            placeholder="+15419990000"
                            value={memberForm.phone}
                            onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                            className="px-4 py-2 text-lg border-2 border-amber-200 rounded-lg"
                            data-testid={`member-phone-${group.id}`}
                          />
                          <button type="submit" className="btn-secondary" data-testid={`add-member-${group.id}`}>
                            Add Member
                          </button>
                        </form>

                        {/* Bulk Upload Section */}
                        <div className="mt-4 pt-4 border-t border-amber-200">
                          <h6 className="text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                            <Upload size={18} className="inline mr-2" />
                            Bulk Upload Members
                          </h6>
                          <p className="text-sm text-slate-600 mb-3">
                            Upload a CSV file - you'll be able to <strong>map columns</strong> before importing
                          </p>
                          <input
                            type="file"
                            accept=".csv"
                            id={`csv-upload-${group.id}`}
                            onChange={(e) => handleCsvPreview(e, group.id)}
                            className="hidden"
                            data-testid={`bulk-upload-input-${group.id}`}
                          />
                          <button
                            onClick={() => document.getElementById(`csv-upload-${group.id}`)?.click()}
                            disabled={bulkUploading}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`bulk-upload-btn-${group.id}`}
                          >
                            {bulkUploading ? (
                              <>Uploading...</>
                            ) : (
                              <><Upload size={18} className="inline mr-2" />Upload CSV</>
                            )}
                          </button>
                          <a 
                            href="/sample-members.csv" 
                            download
                            className="ml-3 text-sm text-blue-600 hover:underline"
                            onClick={(e) => {
                              e.preventDefault();
                              const csv = 'Name,Phone,Email,Notes\nJohn Smith,5419991234,john@example.com,Board member\nJane Doe,5419995678,jane@example.com,Volunteer';
                              const blob = new Blob([csv], { type: 'text/csv' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = 'sample-members.csv';
                              a.click();
                            }}
                          >
                            Download Sample CSV
                          </a>

                          {/* Recent Imports - Undo Section */}
                          {recentImports[group.id]?.length > 0 && (
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <h6 className="text-sm font-semibold text-yellow-800 mb-2">
                                <RotateCcw size={14} className="inline mr-1" />
                                Recent Imports (Undo Available)
                              </h6>
                              <div className="space-y-2">
                                {recentImports[group.id].slice(0, 3).map((imp) => (
                                  <div key={imp.id} className="flex justify-between items-center text-sm">
                                    <span className="text-yellow-700">
                                      {imp.count} members - {new Date(imp.imported_at).toLocaleDateString()}
                                    </span>
                                    <button
                                      onClick={() => handleUndoImport(group.id, imp.id)}
                                      className="text-red-600 hover:text-red-800 text-xs font-semibold"
                                    >
                                      Undo
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* CSV Mapping Modal */}
                      {csvPreview && mappingGroupId === group.id && (
                        <div className="bg-white rounded-lg p-4 mb-4 border-2 border-blue-400 shadow-lg">
                          <div className="flex justify-between items-center mb-4">
                            <h5 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>
                              Map CSV Columns
                            </h5>
                            <button onClick={cancelMapping} className="text-gray-500 hover:text-gray-700">
                              <X size={24} />
                            </button>
                          </div>
                          
                          <p className="text-sm text-slate-600 mb-4">
                            Found <strong>{csvPreview.total_rows}</strong> rows. Map the columns below (First Name and Phone 1 are required):
                          </p>

                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            {/* Name Fields Row */}
                            <div>
                              <label className="block text-sm font-semibold mb-1 text-green-700">First Name *</label>
                              <select
                                value={csvMapping.first_name_column}
                                onChange={(e) => setCsvMapping({ ...csvMapping, first_name_column: e.target.value })}
                                className="w-full px-3 py-2 border-2 border-green-300 rounded-lg bg-green-50"
                              >
                                <option value="">-- Select --</option>
                                {csvPreview.columns.map((col) => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1 text-green-600">Last Name (optional)</label>
                              <select
                                value={csvMapping.last_name_column}
                                onChange={(e) => setCsvMapping({ ...csvMapping, last_name_column: e.target.value })}
                                className="w-full px-3 py-2 border-2 border-green-200 rounded-lg bg-green-50"
                              >
                                <option value="">-- None --</option>
                                {csvPreview.columns.map((col) => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1 text-purple-700">Email (optional)</label>
                              <select
                                value={csvMapping.email_column}
                                onChange={(e) => setCsvMapping({ ...csvMapping, email_column: e.target.value })}
                                className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg bg-purple-50"
                              >
                                <option value="">-- None --</option>
                                {csvPreview.columns.map((col) => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Phone Fields Row */}
                            <div>
                              <label className="block text-sm font-semibold mb-1 text-blue-700">Phone 1 *</label>
                              <select
                                value={csvMapping.phone1_column}
                                onChange={(e) => setCsvMapping({ ...csvMapping, phone1_column: e.target.value })}
                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg bg-blue-50"
                              >
                                <option value="">-- Select --</option>
                                {csvPreview.columns.map((col) => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1 text-blue-600">Phone 2 (optional)</label>
                              <select
                                value={csvMapping.phone2_column}
                                onChange={(e) => setCsvMapping({ ...csvMapping, phone2_column: e.target.value })}
                                className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg bg-blue-50"
                              >
                                <option value="">-- None --</option>
                                {csvPreview.columns.map((col) => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-1 text-orange-700">Notes (optional)</label>
                              <select
                                value={csvMapping.notes_column}
                                onChange={(e) => setCsvMapping({ ...csvMapping, notes_column: e.target.value })}
                                className="w-full px-3 py-2 border-2 border-orange-300 rounded-lg bg-orange-50"
                              >
                                <option value="">-- None --</option>
                                {csvPreview.columns.map((col) => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Preview Table */}
                          <div className="overflow-x-auto mb-4 max-h-64 overflow-y-auto">
                            <p className="text-sm font-semibold mb-2">Preview (first 5 rows):</p>
                            <table className="min-w-full text-sm border">
                              <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                  {csvPreview.columns.map((col) => (
                                    <th key={col} className={`px-3 py-2 border text-left font-semibold ${
                                      col === csvMapping.first_name_column ? 'bg-green-200 text-green-800' :
                                      col === csvMapping.last_name_column ? 'bg-green-100 text-green-700' :
                                      col === csvMapping.phone1_column ? 'bg-blue-200 text-blue-800' :
                                      col === csvMapping.phone2_column ? 'bg-blue-100 text-blue-700' :
                                      col === csvMapping.email_column ? 'bg-purple-200 text-purple-800' :
                                      col === csvMapping.notes_column ? 'bg-orange-200 text-orange-800' : ''
                                    }`}>
                                      {col}
                                      {col === csvMapping.first_name_column && <span className="ml-1">(First)</span>}
                                      {col === csvMapping.last_name_column && <span className="ml-1">(Last)</span>}
                                      {col === csvMapping.phone1_column && <span className="ml-1">(Phone 1)</span>}
                                      {col === csvMapping.phone2_column && <span className="ml-1">(Phone 2)</span>}
                                      {col === csvMapping.email_column && <span className="ml-1">(Email)</span>}
                                      {col === csvMapping.notes_column && <span className="ml-1">(Notes)</span>}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {csvPreview.preview_rows.map((row, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    {csvPreview.columns.map((col) => (
                                      <td key={col} className={`px-3 py-2 border ${
                                        col === csvMapping.first_name_column ? 'bg-green-50' :
                                        col === csvMapping.last_name_column ? 'bg-green-50' :
                                        col === csvMapping.phone1_column ? 'bg-blue-50' :
                                        col === csvMapping.phone2_column ? 'bg-blue-50' :
                                        col === csvMapping.email_column ? 'bg-purple-50' :
                                        col === csvMapping.notes_column ? 'bg-orange-50' : ''
                                      }`}>
                                        {row[col] || <span className="text-gray-400 italic">empty</span>}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={handleMappedUpload}
                              disabled={!csvMapping.first_name_column || !csvMapping.phone1_column || bulkUploading}
                              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                            >
                              {bulkUploading ? 'Importing...' : `Import ${csvPreview.total_rows} Members`}
                            </button>
                            <button
                              onClick={cancelMapping}
                              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 font-semibold"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Members List Header with Bulk Actions */}
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>
                          Group Members ({group.members?.length || 0})
                        </h5>
                        {group.members?.length > 0 && (
                          <div className="flex gap-2">
                            {getSelectedCount(group.id) > 0 && (
                              <button
                                onClick={() => handleBulkDeleteMembers(group.id)}
                                className="bg-red-100 text-red-600 px-3 py-1 rounded-lg text-sm font-semibold hover:bg-red-200"
                              >
                                <Trash2 size={14} className="inline mr-1" />
                                Delete Selected ({getSelectedCount(group.id)})
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteAllMembers(group.id)}
                              className="bg-red-600 text-white px-3 py-1 rounded-lg text-sm font-semibold hover:bg-red-700"
                            >
                              Delete All
                            </button>
                            <button
                              onClick={() => fetchRecentImports(group.id)}
                              className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-lg text-sm font-semibold hover:bg-yellow-200"
                            >
                              <RotateCcw size={14} className="inline mr-1" />
                              Undo Import
                            </button>
                          </div>
                        )}
                      </div>

                      {!group.members || group.members.length === 0 ? (
                        <p className="text-slate-600 text-center py-4">No members in this group yet</p>
                      ) : (
                        <div className="space-y-2">
                          {/* Select All Header */}
                          <div className="flex items-center gap-3 p-2 bg-gray-100 rounded-lg">
                            <button
                              onClick={() => selectAllMembers(group.id, group.members)}
                              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              {group.members.every(m => selectedMembers[group.id]?.[m.phone]) ? (
                                <CheckSquare size={18} />
                              ) : (
                                <Square size={18} />
                              )}
                              Select All
                            </button>
                            <span className="text-sm text-gray-500">
                              ({getSelectedCount(group.id)} selected)
                            </span>
                          </div>

                          {group.members.map((member, idx) => (
                            <div 
                              key={idx} 
                              className={`flex justify-between items-center bg-white p-4 rounded-lg border-2 transition ${
                                selectedMembers[group.id]?.[member.phone] 
                                  ? 'border-blue-400 bg-blue-50' 
                                  : 'border-slate-200 hover:border-blue-400'
                              }`}
                              data-testid={`member-${group.id}-${idx}`}
                            >
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => toggleMemberSelection(group.id, member.phone)}
                                  className="text-blue-600"
                                >
                                  {selectedMembers[group.id]?.[member.phone] ? (
                                    <CheckSquare size={22} />
                                  ) : (
                                    <Square size={22} />
                                  )}
                                </button>
                                <div className="bg-blue-100 p-2 rounded-full">
                                  <Users size={20} color="#1e3a8a" />
                                </div>
                                <div>
                                  <p className="text-lg font-semibold text-slate-800">{member.name}</p>
                                  <p className="text-slate-600">{member.phone}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveMember(group.id, member.phone)}
                                className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition"
                                data-testid={`remove-member-${group.id}-${idx}`}
                              >
                                <UserMinus size={20} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Send Message Tab */}
      {activeTab === 'send' && (
        <div className="bg-white rounded-xl p-8 shadow-md max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Send size={32} color="#1e3a8a" />
            <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>Send or Schedule Message</h3>
          </div>

          {smsAccountStatus?.warning && (
            <div
              data-testid="sms-account-warning"
              className="mb-6 border-2 rounded-lg p-4 flex items-start gap-3"
              style={{
                borderColor: smsAccountStatus.balance !== null && smsAccountStatus.balance <= 0 ? '#dc2626' : '#f59e0b',
                background: smsAccountStatus.balance !== null && smsAccountStatus.balance <= 0 ? '#fef2f2' : '#fffbeb'
              }}
            >
              <XCircle
                size={24}
                color={smsAccountStatus.balance !== null && smsAccountStatus.balance <= 0 ? '#dc2626' : '#f59e0b'}
                className="flex-shrink-0 mt-0.5"
              />
              <div>
                <p className="font-semibold text-base mb-1" style={{ color: '#1e3a8a' }}>
                  {smsAccountStatus.balance !== null && smsAccountStatus.balance <= 0
                    ? 'Vonage account balance depleted'
                    : 'Vonage account warning'}
                </p>
                <p className="text-sm text-slate-700">{smsAccountStatus.warning}</p>
                {smsAccountStatus.balance !== null && smsAccountStatus.balance <= 0 && (
                  <a
                    href="https://dashboard.nexmo.com/billing-and-payments"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-2 text-sm font-semibold underline"
                    style={{ color: '#dc2626' }}
                    data-testid="vonage-topup-link"
                  >
                    Top up Vonage account →
                  </a>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSendMessage} className="space-y-6">
            {/* Group Selection - Multi-select */}
            <div>
              <label className="block text-lg font-semibold mb-3" style={{ color: '#1e3a8a' }}>Select Groups *</label>
              
              {/* Select All Option */}
              <div className="mb-4">
                <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                  messageForm.send_to_all 
                    ? 'bg-green-100 border-green-500' 
                    : 'bg-gray-50 border-gray-200 hover:border-green-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={messageForm.send_to_all}
                    onChange={handleSelectAllGroups}
                    className="w-5 h-5"
                    data-testid="select-all-groups"
                  />
                  <span className="text-lg font-semibold text-green-700">
                    📢 Send to ALL Groups ({groups.reduce((acc, g) => {
                      const phones = new Set();
                      g.members?.forEach(m => phones.add(m.phone));
                      return acc + phones.size;
                    }, 0)} unique recipients)
                  </span>
                </label>
              </div>

              {/* Individual Group Selection */}
              {!messageForm.send_to_all && (
                <div className="grid md:grid-cols-2 gap-3">
                  {groups.map((group) => (
                    <label 
                      key={group.id}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                        messageForm.group_ids.includes(group.id)
                          ? 'bg-blue-100 border-blue-500'
                          : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={messageForm.group_ids.includes(group.id)}
                        onChange={() => handleGroupSelect(group.id)}
                        className="w-5 h-5"
                        data-testid={`group-checkbox-${group.id}`}
                      />
                      <div>
                        <span className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>{group.name}</span>
                        <span className="text-slate-500 ml-2">({group.members?.length || 0} members)</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Recipients Summary */}
            {(messageForm.group_ids.length > 0 || messageForm.send_to_all) && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-900">
                  <Users size={20} />
                  <span className="font-semibold text-lg">
                    Will send to {getTotalRecipients()} unique recipients
                    {messageForm.send_to_all ? ' (All Groups)' : ` across ${messageForm.group_ids.length} group(s)`}
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Message *</label>
              <textarea
                required
                rows={5}
                value={messageForm.message}
                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg"
                placeholder="Type your message here..."
                data-testid="sms-message-input"
                maxLength={612}
              />
              <p className="text-right text-slate-600 mt-1" data-testid="sms-char-counter">{messageForm.message.length}/612 characters</p>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={24} color="#f59e0b" />
                <label className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>Schedule for Later (Optional)</label>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-700">Date</label>
                  <input
                    type="date"
                    value={messageForm.scheduled_date}
                    onChange={(e) => setMessageForm({ ...messageForm, scheduled_date: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-300 rounded-lg"
                    data-testid="sms-schedule-date"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-700">Time</label>
                  <input
                    type="time"
                    value={messageForm.scheduled_time}
                    onChange={(e) => setMessageForm({ ...messageForm, scheduled_time: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-300 rounded-lg"
                    data-testid="sms-schedule-time"
                  />
                </div>
              </div>
              <p className="text-slate-600 mt-3">
                <Clock size={16} className="inline mr-1" />
                Timezone: <strong>{timezone.replace('_', ' ')}</strong>
                {messageForm.scheduled_date && messageForm.scheduled_time && (
                  <span className="ml-3 text-amber-700 font-semibold">
                    Will send at {formatTime12Hour(messageForm.scheduled_time)} on {new Date(messageForm.scheduled_date + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </span>
                )}
              </p>
              <p className="text-slate-500 text-sm mt-1">Leave both blank to send immediately</p>
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full text-xl" 
              data-testid="sms-send-btn"
              disabled={!messageForm.group_ids.length && !messageForm.send_to_all}
            >
              <Send size={24} className="inline mr-2" />
              {(messageForm.scheduled_date && messageForm.scheduled_time) ? 'Schedule Message' : 'Send Now'}
            </button>
          </form>
        </div>
      )}

      {/* Scheduled & History tabs remain the same... */}
      {activeTab === 'scheduled' && (
        <div className="space-y-4 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Clock size={32} color="#f59e0b" />
              <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>Scheduled Messages</h3>
            </div>
            <div className="text-sm text-slate-500 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
              <CheckCircle size={16} className="inline mr-1 text-green-600" />
              Auto-sending enabled (checks every minute)
            </div>
          </div>

          {scheduledMessages.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-md">
              <Clock size={64} color="#cbd5e1" className="mx-auto mb-4" />
              <p className="text-xl text-slate-600">No scheduled messages</p>
              <p className="text-slate-500">Messages scheduled for future dates will appear here</p>
              <p className="text-slate-400 text-sm mt-4">The system automatically checks and sends messages every minute</p>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduledMessages.map((msg) => (
                <div key={msg.id} className="bg-white border-2 border-amber-200 rounded-lg p-6 shadow-md" data-testid={`scheduled-msg-${msg.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock size={20} color="#f59e0b" />
                        <span className="text-lg font-bold text-amber-600">
                          Scheduled for: {formatDateTime(msg.scheduled_for)}
                          {msg.timezone && <span className="text-sm font-normal ml-2">({msg.timezone.replace('_', ' ')})</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <Users size={18} color="#1e3a8a" />
                        <span className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                          {msg.group_name} ({msg.recipients_count} recipients)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelScheduled(msg.id)}
                      className="bg-red-100 text-red-600 hover:bg-red-200 px-4 py-2 rounded-lg font-semibold transition"
                      data-testid={`cancel-scheduled-${msg.id}`}
                    >
                      <Trash2 size={20} className="inline mr-2" />
                      Cancel
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-amber-300">
                    <p className="text-lg text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare size={32} color="#10b981" />
            <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>Message History</h3>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl p-6 shadow-md mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Month Filter */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                  <Filter size={16} className="inline mr-2" />
                  Filter by Month
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-amber-200 rounded-lg"
                  data-testid="month-filter"
                >
                  <option value="">All Time</option>
                  {availableMonths.map((month) => (
                    <option key={month} value={month}>
                      {formatMonthDisplay(month)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                  <Search size={16} className="inline mr-2" />
                  Search Messages
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchMessages()}
                    placeholder="Search by keyword..."
                    className="flex-1 px-4 py-2 border-2 border-amber-200 rounded-lg"
                    data-testid="message-search"
                  />
                  <button
                    onClick={handleSearchMessages}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Search size={20} />
                  </button>
                </div>
              </div>

              {/* Show Hidden Toggle */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                  <Eye size={16} className="inline mr-2" />
                  Visibility
                </label>
                <label className="flex items-center gap-3 px-4 py-2 border-2 border-amber-200 rounded-lg cursor-pointer hover:bg-amber-50">
                  <input
                    type="checkbox"
                    checked={showHidden}
                    onChange={(e) => setShowHidden(e.target.checked)}
                    className="w-5 h-5"
                    data-testid="show-hidden-toggle"
                  />
                  <span>Show Hidden Messages</span>
                </label>
              </div>
            </div>

            {/* Clear Filters Button */}
            {(selectedMonth || searchQuery || showHidden) && (
              <div className="mt-4">
                <button
                  onClick={() => {
                    setSelectedMonth('');
                    setSearchQuery('');
                    setShowHidden(false);
                    fetchMessageHistory();
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {messageHistory.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-md">
              <MessageSquare size={64} color="#cbd5e1" className="mx-auto mb-4" />
              <p className="text-xl text-slate-600">No messages found</p>
              <p className="text-slate-500">
                {selectedMonth || searchQuery ? 'Try adjusting your filters' : 'Sent messages will appear here with full details'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-600 text-sm">
                Showing {messageHistory.length} message{messageHistory.length !== 1 ? 's' : ''}
                {selectedMonth && ` from ${formatMonthDisplay(selectedMonth)}`}
                {searchQuery && ` matching "${searchQuery}"`}
              </p>
              
              {messageHistory.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`rounded-lg p-6 shadow-md ${
                    msg.hidden 
                      ? 'bg-gray-100 border-2 border-gray-300 opacity-70'
                      : msg.status === 'sent' 
                        ? 'bg-white border-2 border-green-300' 
                        : 'bg-white border-2 border-red-300'
                  }`}
                  data-testid={`history-msg-${msg.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      {msg.hidden && (
                        <div className="flex items-center gap-2 mb-2 text-gray-500">
                          <EyeOff size={16} />
                          <span className="text-sm font-semibold">Hidden</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-3">
                        {msg.status === 'sent' ? (
                          <>
                            <CheckCircle size={24} color="#10b981" />
                            <span className="text-lg font-bold text-green-600">Successfully Sent</span>
                          </>
                        ) : (
                          <>
                            <XCircle size={24} color="#ef4444" />
                            <span className="text-lg font-bold text-red-600">Failed</span>
                          </>
                        )}
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar size={18} color="#64748b" />
                          <span className="text-lg text-slate-700">Sent: <strong>{formatDateTime(msg.sent_at)}</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users size={18} color="#64748b" />
                          <span className="text-lg text-slate-700">Group: <strong>{msg.group_name}</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={18} color="#64748b" />
                          <span className="text-lg text-slate-700">
                            Delivered: <strong className="text-green-600">{msg.recipients_count} recipients</strong>
                            {msg.failed_count > 0 && (
                              <span className="text-red-600 ml-2">({msg.failed_count} failed)</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {msg.hidden ? (
                        <button
                          onClick={() => handleUnhideMessage(msg.id)}
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 px-3 py-2 rounded-lg font-semibold transition"
                          title="Unhide message"
                          data-testid={`unhide-msg-${msg.id}`}
                        >
                          <Eye size={18} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleHideMessage(msg.id)}
                          className="bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-2 rounded-lg font-semibold transition"
                          title="Hide message"
                          data-testid={`hide-msg-${msg.id}`}
                        >
                          <EyeOff size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        className="bg-red-100 text-red-600 hover:bg-red-200 px-3 py-2 rounded-lg font-semibold transition"
                        title="Delete permanently"
                        data-testid={`delete-msg-${msg.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg border-2 border-slate-200">
                    <p className="text-slate-600 text-sm mb-1">Message Content:</p>
                    <p className="text-lg text-slate-800 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Opt-Outs Tab */}
      {activeTab === 'optouts' && (
        <div className="space-y-6 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <UserMinus size={32} color="#dc2626" />
            <h3 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>SMS Opt-Outs</h3>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
            <h4 className="text-lg font-bold text-red-800 mb-2">How Opt-Outs Work</h4>
            <ul className="text-red-700 space-y-1 text-sm">
              <li>• When someone replies <strong>STOP</strong> to any text message, they are automatically added here</li>
              <li>• Phone numbers on this list will NOT receive any SMS messages from your church</li>
              <li>• You can manually add or remove phone numbers below</li>
              <li>• Restoring a phone number allows them to receive messages again</li>
            </ul>
          </div>

          {/* Add Manual Opt-Out */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h4 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>
              <UserMinus size={20} className="inline mr-2" />
              Manually Add Opt-Out
            </h4>
            <form onSubmit={handleAddOptout} className="flex gap-3">
              <input
                type="tel"
                value={newOptoutPhone}
                onChange={(e) => setNewOptoutPhone(e.target.value)}
                placeholder="Enter phone number (e.g., 5419991234)"
                className="flex-1 px-4 py-3 border-2 border-amber-200 rounded-lg text-lg"
                data-testid="optout-phone-input"
              />
              <button 
                type="submit" 
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                data-testid="add-optout-btn"
              >
                <UserMinus size={20} className="inline mr-2" />
                Add to Opt-Out List
              </button>
            </form>
          </div>

          {/* Opt-Out List */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-red-600 text-white px-6 py-4">
              <h4 className="text-xl font-bold">
                Opted-Out Phone Numbers ({optouts.length})
              </h4>
              <p className="text-red-100 text-sm">These numbers will NOT receive any SMS messages</p>
            </div>
            
            {optouts.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle size={64} color="#10b981" className="mx-auto mb-4" />
                <p className="text-xl text-slate-600">No opt-outs yet</p>
                <p className="text-slate-500">When someone replies STOP, they'll appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {optouts.map((optout) => (
                  <div 
                    key={optout.phone} 
                    className="flex justify-between items-center p-4 hover:bg-gray-50"
                    data-testid={`optout-${optout.phone}`}
                  >
                    <div>
                      <p className="text-lg font-semibold text-slate-800">{optout.phone}</p>
                      <p className="text-sm text-slate-500">
                        Opted out: {new Date(optout.opted_out_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })} • {optout.reason}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveOptout(optout.phone)}
                      className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-semibold hover:bg-green-200 transition"
                      data-testid={`restore-${optout.phone}`}
                    >
                      <UserPlus size={18} className="inline mr-1" />
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SMSDashboard;
